import { computed, Ref } from "vue";
import { CustomWidgetModel } from "../models";
import { ITextEditorFile } from "../interfaces";
import { TextEditorFileWidget } from "../components";

type EditorWidgetsEmit<T extends string | number> = {
  (e: "onClickDeleteFile", fileId: T): void;
  (e: "onClickEditFile", fileId: T): void;
  (e: "saveFileName", fileId: T, fileName: string): void;
};

type UseEditorWidgetsOptions<
  T1 extends ITextEditorFile,
  T2 extends EditorWidgetsEmit<T1["FileId"]>
> = {
  /**Контент редактора */
  content: Ref<string>;
  /**Список файлов */
  files: Ref<Array<T1>>;
  /**Загрузка при редактировании названия файла */
  filesLoadingEditName: Ref<Array<T1["FileId"]>>;
  /**Функции emit */
  emit: T2;
};

/**
 * * Управление виджетами в редакторе
 * @param content Контент в редакторе
 * @param files Список файлов
 * @returns
 */
export const useEditorWidgets = <
  T1 extends ITextEditorFile,
  T2 extends EditorWidgetsEmit<T1["FileId"]>
>({
  content,
  files,
  filesLoadingEditName,
  emit,
}: UseEditorWidgetsOptions<T1, T2>) => {
  /**
   * * Получить идентификаторы виджетов
   * @param html
   */
  const extractWidgetIdsFromHTML = (html: string): string[] => {
    // Создаем объект для парсинга HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Ищем все элементы с атрибутом data-widget-id
    const elements = doc.querySelectorAll("[data-widget-id]");

    // Извлекаем значения атрибута data-widget-id и возвращаем в виде массива
    const widgetIds: string[] = [];
    elements.forEach((element) => {
      const widgetId = element.getAttribute("data-widget-id");
      if (widgetId) {
        widgetIds.push(widgetId);
      }
    });

    return widgetIds;
  };
  /**
   * * Удалить виджеты из HTML
   * @param html
   * @returns
   */
  const removeWidgetsFromHTML = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Ищем все элементы с атрибутом data-widget-id
    const elements = doc.querySelectorAll("[data-widget-id]");
    elements.forEach((element) => {
      element.innerHTML = "";
    });

    return doc.body.innerHTML;
  };

  const widgetsIds = computed(
    () => content.value?.trim() && extractWidgetIdsFromHTML(content.value)
  );
  /**Список файлов в редакторе */
  const filesInEditor = computed(() =>
    files.value.filter((file) =>
      widgetsIds.value.includes(file.FileId.toString())
    )
  );

  /**Список виджетов для отображения */
  const widgets = computed(() => {
    if (!widgetsIds.value?.length) return [];
    const widgetsFiles = new Array<
      CustomWidgetModel<{ file: ITextEditorFile }>
    >();

    filesInEditor.value.forEach((fileInfo) => {
      widgetsFiles.push(
        new CustomWidgetModel({
          DataId: fileInfo.FileId.toString(),
          Component: TextEditorFileWidget,
          Props: {
            file: fileInfo,
            files: filesInEditor.value,
            filesLoadingEditName: filesLoadingEditName.value,
          },
          Listeners: {
            onClickDeleteFile: () => emit("onClickDeleteFile", fileInfo.FileId),
            onClickEditFile: (fileId: number) =>
              emit("onClickEditFile", fileId),
            saveName: (fileId: number, fileName: string) =>
              emit("saveFileName", fileId, fileName),
          },
        })
      );
    });

    return [...widgetsFiles];
  });

  return {
    /**Список виджетов */
    widgets,
    /**Удалить виджеты из HTML */
    removeWidgetsFromHTML,
  };
};
